import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

const CATEGORY_LABEL = {
    recomendaciones: "recommendations",
    autores: "authors",
    generos: "genres",
    novedades: "new",
    librerias: "bookshops",
};

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        return new Intl.DateTimeFormat(lang, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(iso));
    } catch {
        return iso.slice(0, 10);
    }
}

export default function BlogPost() {
    const { t, i18n } = useTranslation();
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/blog/posts/${slug}`);
                if (alive) setPost(data);
            } catch (e) {
                if (!alive) return;
                if (e.response?.status === 404) setError("not_found");
                else setError(formatApiError(e.response?.data?.detail) || "error");
            }
        })();
        return () => {
            alive = false;
        };
    }, [slug]);

    if (error === "not_found") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <p
                    className="font-display text-3xl text-[var(--ink)] mb-3"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}
                >
                    {t("blog.not_found_title")}
                </p>
                <Link
                    to="/blog"
                    data-testid="blog-post-fallback-cta"
                    className="mt-6 inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2.5 text-xs uppercase tracking-widest"
                >
                    <ArrowLeft size={13} strokeWidth={1.5} />
                    {t("blog.back_to_index")}
                </Link>
            </main>
        );
    }

    if (!post) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center">
                <p className="font-display text-2xl text-[var(--ink)] animate-slow-pulse italic">
                    {t("blog.loading")}
                </p>
            </main>
        );
    }

    const title = post.seo_title || `${post.title} · RecomendaLibros`;
    const description = post.seo_description || post.excerpt;
    const canonical = `https://recomendalibros.com/blog/${post.slug}`;

    // JSON-LD BlogPosting — gives Google rich snippets for article carousels.
    const articleLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        inLanguage: "es",
        url: canonical,
        datePublished: post.published_at || post.updated_at,
        dateModified: post.updated_at,
        author: { "@type": "Organization", name: post.author_name || "RecomendaLibros" },
        publisher: {
            "@type": "Organization",
            name: "RecomendaLibros",
            logo: {
                "@type": "ImageObject",
                url: "https://recomendalibros.com/og-home.jpg",
            },
        },
        mainEntityOfPage: canonical,
        ...(post.cover_url ? { image: post.cover_url } : {}),
        articleSection: post.category,
        keywords: (post.tags || []).join(", "),
    };

    return (
        <main
            data-testid="blog-post"
            className="min-h-[calc(100vh-73px)] max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24"
        >
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonical} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonical} />
                {post.cover_url && <meta property="og:image" content={post.cover_url} />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                {post.cover_url && <meta name="twitter:image" content={post.cover_url} />}
                <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
            </Helmet>

            <div className="mb-8">
                <Link
                    to="/blog"
                    data-testid="blog-post-back"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
                >
                    <ArrowLeft size={12} strokeWidth={1.8} />
                    {t("blog.back_to_index")}
                </Link>
            </div>

            <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-4">
                {t(`blog.cat_${CATEGORY_LABEL[post.category] || "all"}`)}
            </p>

            <h1
                data-testid="blog-post-title"
                className="font-display text-4xl md:text-6xl text-[var(--ink)] italic leading-[1.05] tracking-tight mb-6"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 144, "slnt" -8' }}
            >
                {post.title}
            </h1>

            <div className="flex items-center gap-4 text-xs text-[var(--ink-dim)] mb-12 pb-8 border-b border-[var(--border)]">
                <span>{formatDate(post.published_at, i18n.language)}</span>
                <span className="inline-flex items-center gap-1">
                    <Clock size={11} strokeWidth={1.8} />
                    {t("blog.read_min", { count: post.read_time_min || 1 })}
                </span>
                <span>·</span>
                <span>{post.author_name}</span>
            </div>

            {post.cover_url && (
                <figure className="mb-12">
                    <img
                        src={post.cover_url}
                        alt={post.title}
                        className="w-full h-auto border border-[var(--border-strong)]"
                    />
                </figure>
            )}

            <article
                data-testid="blog-post-body"
                className="blog-content text-[var(--ink)] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content_html }}
            />

            {(post.tags || []).length > 0 && (
                <div className="mt-16 pt-8 border-t border-[var(--border)] flex flex-wrap gap-2">
                    {post.tags.map((tg) => (
                        <span
                            key={tg}
                            className="inline-block px-3 py-1 border border-[var(--border-strong)] rounded-full text-[10px] uppercase tracking-widest text-[var(--ink-muted)]"
                        >
                            #{tg}
                        </span>
                    ))}
                </div>
            )}

            <div className="mt-12 flex flex-wrap items-center gap-4">
                <ShareButton recId={post.slug} basedOn={post.title} url={canonical} />
            </div>
        </main>
    );
}
